<template>
  <v-card class="payments-list full-height" :flat="flat">
    <v-card-title v-if="showTitle" :class="{ 'py-2': shrinkWithoutPayments && !hasPayments, 'pb-0 pt-2': denseHeader && !(shrinkWithoutPayments && !hasPayments) }">
      <div class="card-title">Betalningar</div>
      <template v-if="loading">
        <v-progress-circular
          indeterminate
          size="40"
          class="ml-4"
          color="primary"
        ></v-progress-circular>
      </template>
      <template v-else>
        <v-alert
          v-show="shrinkWithoutPayments && !hasPayments"
          dense
          text
          color="info"
          class="ma-0 ml-4 py-1"
        >
          Det finns inga betalningar
        </v-alert>
      </template>
    </v-card-title>

    <div class="overflow-y-auto" :style="{ height: scrollableTableHeight }">
      <v-data-table
        v-show="hasPayments || !shrinkWithoutPayments"
        :headers="headers"
        :items="tableRows"
        @click:row="paymentClicked"
        fixed-header
        :loading="loading"
        hide-default-footer
        disable-filtering
        disable-pagination
        :item-class="() => ({ 'cursor-pointer': true })"
      >
        <template v-slot:no-data>
          <template v-if="loading">
            <v-simple-table class="skeleton-loader-table">
              <tr v-for="i in 2" :key="i">
                <td v-for="(header, x) in headers" :key="x" :class="{ 'text-left': header.align === 'start', 'text-right': header.align === 'end' }">
                  <v-skeleton-loader type="table-cell"></v-skeleton-loader>
                </td>
              </tr>
            </v-simple-table>
          </template>
          <template v-else>
            <v-alert
              dense
              text
              color="info"
              class="ma-10 text-center"
            >
              Det finns inga betalningar att visa
            </v-alert>
          </template>
        </template>

        <template v-slot:[`item.paidAtFormatted`]="{ item }">
          <div class="text-left">
            <div>{{ item.paidAtFormatted }}</div>
            <div v-if="item.isDeleted" class="text-caption text--disabled text-sm">
              (Ångrad {{ item.deletedAtFormatted }})
            </div>
            <div v-else-if="item.isRefunded" class="text-caption text--disabled text-sm">
              (Återbetald {{ item.refundedAtFormatted }})
            </div>
          </div>
        </template>

        <template v-slot:[`item.amountFormatted`]="{ item }">
          <div class="text-right" :class="{ 'text-decoration-line-through': item.isDeleted }">
            {{ item.amountFormatted }}
          </div>
        </template>

        <template v-slot:[`item.paymentMethod`]="{ item }">
          <div class="d-flex align-center">
            <v-icon
              v-if="item.icon"
              small
              class="mr-2"
              :color="item.color || 'secondary'"
            >{{ item.icon }}</v-icon>
            <span>{{ item.paymentMethod }}</span>
          </div>
        </template>

        <template v-slot:[`item.documentLabel`]="{ item }">
          <div class="d-flex align-center">
            <v-chip
              small
              outlined
              :color="item.documentType === 'invoice' ? 'secondary' : 'success'"
              class="mr-2"
            >
              {{ item.documentType === 'invoice' ? 'Faktura' : 'Kvitto' }}
            </v-chip>
            <span>{{ item.documentLabel }}</span>
          </div>
        </template>

        <template v-if="showTotalRow && hasPayments" v-slot:[`body.append`]>
          <tr class="payments-list-summary-row">
            <td
              v-for="header in headers"
              :key="header.value"
              :class="getSummaryCellClass(header)"
            >
              {{ getSummaryCellValue(header) }}
            </td>
          </tr>
        </template>
      </v-data-table>
    </div>
  </v-card>
</template>

<style scoped lang="scss">
.payments-list-summary-row td {
  background-color: #fafafa;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  font-weight: 500;
}
</style>

<script>
export default {
  name: 'PaymentsList',
  props: {
    receiptPayments: {
      type: Array,
      default: () => []
    },
    invoicePaymentApplications: {
      type: Array,
      default: () => []
    },
    loading: Boolean,
    showTitle: Boolean,
    denseHeader: Boolean,
    flat: Boolean,
    shrinkWithoutPayments: Boolean,
    showTotalRow: {
      type: Boolean,
      default: false
    },
    displayedItems: {
      type: Number,
      default: function () { return 0 }
    },
    displayedColumns: {
      type: Array,
      default: function () { return ['paid_at', 'amount', 'payment_method', 'document'] }
    }
  },
  computed: {
    headers: function () {
      const columns = []
      if (this.displayedColumns.includes('paid_at')) {
        columns.push({ text: 'Betald', value: 'paidAtFormatted', align: 'start', sortable: false })
      }
      if (this.displayedColumns.includes('amount')) {
        columns.push({ text: 'Belopp', value: 'amountFormatted', align: 'end', sortable: false })
      }
      if (this.displayedColumns.includes('payment_method')) {
        columns.push({ text: 'Betalsätt', value: 'paymentMethod', align: 'start', sortable: false })
      }
      if (this.displayedColumns.includes('document')) {
        columns.push({ text: 'Tillhör', value: 'documentLabel', align: 'start', sortable: false })
      }
      return columns
    },
    tableRows: function () {
      const receiptRows = this.receiptPayments.map(payment => {
        return {
          clientId: `receipt-${payment.id}`,
          documentType: 'receipt',
          documentId: payment.receipt?.id || payment.receipt_id,
          paidAt: payment.created_at,
          paidAtFormatted: payment.paidAtFormatted,
          amount: payment.paid_amount || 0,
          amountFormatted: payment.amountFormatted,
          paymentMethod: payment.paymentMethodLabel,
          icon: payment.iconClassName,
          color: payment.color,
          documentLabel: payment.documentLabel,
          isRefunded: payment.isRefunded, // Återbetalningen har en egen rad så dessa ska inte filtreras bort från totalen
          refundedAtFormatted: payment.refundedAtFormatted
        }
      })

      const invoiceRows = this.invoicePaymentApplications.map(paymentApplication => {
        return {
          clientId: `invoice-${paymentApplication.id}`,
          documentType: 'invoice',
          documentId: paymentApplication.applicable_id,
          paidAt: paymentApplication.paidAt,
          paidAtFormatted: paymentApplication.paidAtFormatted,
          amount: paymentApplication.paid_amount || 0,
          amountFormatted: paymentApplication.amountFormatted,
          paymentMethod: paymentApplication.paymentMethodLabel,
          icon: paymentApplication.iconClassName,
          color: paymentApplication.color,
          documentLabel: paymentApplication.documentLabel,
          isDeleted: paymentApplication.deleted_at !== null,
          deletedAtFormatted: paymentApplication.deletedAtFormatted
        }
      })

      return receiptRows.concat(invoiceRows).sort((a, b) => {
        if (a.paidAt.isSame(b.paidAt)) {
          return 0
        }
        return a.paidAt.isBefore(b.paidAt) ? 1 : -1
      })
    },
    totalAmountSummary: function () {
      return this.tableRows.filter(payment => !payment.isDeleted).reduce((totalAmount, payment) => {
        return totalAmount + (payment.amount || 0)
      }, 0)
    },
    summaryLabelColumnValue: function () {
      const labelColumn = this.headers.find(header => {
        return header.value !== 'amountFormatted'
      })
      return labelColumn?.value || null
    },
    paymentCountSummary: function () {
      const paymentCount = this.tableRows.length
      return `${paymentCount} ${paymentCount === 1 ? 'betalning' : 'betalningar'}`
    },
    hasPayments: function () {
      return this.tableRows.length > 0
    },
    scrollableTableHeight: function () {
      if (!this.hasPayments && this.showTitle) {
        return '0px'
      }
      if (this.displayedItems === 0) {
        return '100%'
      }
      const requiredHeight = this.tableRows.length * 48
      const maxHeight = this.displayedItems * 48
      let height = Math.min(requiredHeight, maxHeight)
      if (requiredHeight > maxHeight) {
        height += 24
      }
      height += 48

      height += 100

      return `${height}px`
    }
  },
  methods: {
    paymentClicked: function ({ documentType, documentId }) {
      if (documentType === 'invoice') {
        this.openInvoice(documentId)
        return
      }
      this.openReceipt(documentId)
    },
    openInvoice: function (invoiceId) {
      this.$store.commit('invoice/SET_SHOW_DIALOG', false)
      this.$nextTick(() => {
        this.$store.dispatch('invoice/openDialogById', { invoiceId })
      })
    },
    openReceipt: function (receiptId) {
      this.$store.commit('posReceipt/SET_SHOW_DIALOG', false)
      this.$nextTick(() => {
        this.$store.dispatch('posReceipt/openDialogById', { receiptId })
      })
    },
    getSummaryCellValue: function (header) {
      if (header.value === this.summaryLabelColumnValue) {
        return `Summa (${this.paymentCountSummary})`
      }
      if (header.value === 'amountFormatted') {
        return window.conversion.toHumanReadableAmount(this.totalAmountSummary / 100)
      }
      return ''
    },
    getSummaryCellClass: function (header) {
      return {
        'text-left': header.align === 'start' || header.value === this.summaryLabelColumnValue,
        'text-right': header.align === 'end' || header.value === 'amountFormatted'
      }
    }
  }
}
</script>
